/**
 * Routing, hand written.
 *
 * Hash routes, so a deep link survives a reload without the Worker having to
 * know anything, and so the one-time parameters a magic link puts in the query
 * string never collide with the path.
 */

import { useEffect, useState } from "react";

export type Route =
  | { name: "home" }
  | { name: "discover" }
  | { name: "apply" }
  | { name: "status"; id: string }
  | { name: "messages"; id: string }
  | { name: "letters" }
  | { name: "help" };

export const HOME = "#/";
export const DISCOVER = "#/what-do-i-need";
export const APPLY = "#/apply";
export const LETTERS = "#/letters";
export const HELP = "#/how-to-use-this";
export const statusHref = (id: string): string => `#/application/${encodeURIComponent(id)}`;
export const messagesHref = (id: string): string =>
  `#/application/${encodeURIComponent(id)}/messages`;

export function parseRoute(hash: string): Route {
  const path = hash.replace(/^#\/?/, "").split("?")[0] ?? "";
  const parts = path.split("/").filter((p) => p.length > 0);

  if (parts.length === 0) return { name: "home" };
  if (parts[0] === "what-do-i-need") return { name: "discover" };
  if (parts[0] === "apply") return { name: "apply" };
  if (parts[0] === "letters") return { name: "letters" };
  if (parts[0] === "how-to-use-this") return { name: "help" };
  if (parts[0] === "application" && parts[1] !== undefined) {
    const id = decodeURIComponent(parts[1]);
    if (parts[2] === "messages") return { name: "messages", id };
    return { name: "status", id };
  }
  return { name: "home" };
}

export function navigate(href: string): void {
  if (window.location.hash === href) return;
  window.location.hash = href;
}

export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(() => parseRoute(window.location.hash));

  useEffect(() => {
    const onChange = (): void => {
      setRoute(parseRoute(window.location.hash));
      window.scrollTo({ top: 0 });
    };
    window.addEventListener("hashchange", onChange);
    return () => window.removeEventListener("hashchange", onChange);
  }, []);

  return route;
}
